/** Login screen — 1:1 port z prototype/login.jsx + napojené na Supabase OTP. */

import { useEffect, useRef, useState, type FormEvent } from "react";
import { Navigate, useLocation, useNavigate } from "react-router";
import { Wordmark } from "@/components/ui/wordmark";
import { Ic } from "@/components/ui/icons";
import { Button } from "@/components/ui/button";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/auth/auth-provider";
import { t } from "@/i18n/de";

type Step = "email" | "code";

export function LoginRoute() {
  const navigate = useNavigate();
  const location = useLocation();
  const { session, loading } = useAuth();
  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const codeInputRef = useRef<HTMLInputElement | null>(null);
  const isMobile = typeof window !== "undefined" && window.innerWidth < 480;

  useEffect(() => {
    if (step === "code") {
      setTimeout(() => codeInputRef.current?.focus(), 80);
    }
  }, [step]);

  if (!loading && session) {
    const redirectTo =
      (location.state as { from?: { pathname?: string } } | null)?.from?.pathname || "/";
    return <Navigate to={redirectTo} replace />;
  }

  const submitEmail = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      const { error: err } = await supabase.auth.signInWithOtp({
        email: email.trim(),
        options: {
          shouldCreateUser: false,
          emailRedirectTo: `${window.location.origin}/`,
        },
      });
      if (err) throw err;
      setStep("code");
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      // Mapování typických Supabase chyb na DE
      if (/rate|too many/i.test(msg)) {
        setError("Zu viele Anfragen. Bitte versuchen Sie es in einer Minute erneut.");
      } else if (/not allowed|signups not allowed/i.test(msg)) {
        setError(
          "Diese E-Mail ist nicht für den Portal-Zugang freigeschaltet. Bitte kontaktieren Sie das Backoffice.",
        );
      } else if (/invalid|valid email/i.test(msg)) {
        setError("Ungültige E-Mail-Adresse.");
      } else {
        setError(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const verifyCode = async (token: string) => {
    setSubmitting(true);
    setError(null);
    try {
      const { error: err } = await supabase.auth.verifyOtp({
        email: email.trim(),
        token,
        type: "email",
      });
      if (err) throw err;
      const redirectTo =
        (location.state as { from?: { pathname?: string } } | null)?.from?.pathname || "/";
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(t.login.invalidCode);
      setCode("");
      setTimeout(() => codeInputRef.current?.focus(), 50);
      void err;
    } finally {
      setSubmitting(false);
    }
  };

  const submitCode = (e?: FormEvent) => {
    e?.preventDefault?.();
    const trimmed = code.trim();
    if (trimmed.length < 6 || trimmed.length > 10) return;
    void verifyCode(trimmed);
  };

  const requestResend = async () => {
    if (!email) return;
    setError(null);
    try {
      const { error: err } = await supabase.auth.signInWithOtp({
        email: email.trim(),
        options: {
          shouldCreateUser: false,
          emailRedirectTo: `${window.location.origin}/`,
        },
      });
      if (err) throw err;
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <div
      style={{
        width: "100%",
        minHeight: "100vh",
        background: `
          radial-gradient(60% 50% at 50% 0%, rgba(201,169,107,0.08), transparent 70%),
          radial-gradient(40% 30% at 50% 100%, rgba(201,169,107,0.04), transparent 70%),
          var(--bg-base)`,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: isMobile ? "24px 20px" : "48px",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "linear-gradient(var(--border-subtle) 1px, transparent 1px), linear-gradient(90deg, var(--border-subtle) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
          maskImage: "radial-gradient(60% 60% at 50% 50%, #000, transparent)",
          WebkitMaskImage: "radial-gradient(60% 60% at 50% 50%, #000, transparent)",
          opacity: 0.5,
          pointerEvents: "none",
        }}
      />

      <div style={{ position: "relative", zIndex: 1, marginBottom: isMobile ? 32 : 56 }}>
        <Wordmark size={isMobile ? 32 : 44} />
      </div>

      <div
        style={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          maxWidth: isMobile ? 360 : 420,
          background: "var(--bg-card)",
          border: "1px solid var(--border)",
          borderRadius: "var(--r-xl)",
          padding: isMobile ? 24 : 36,
          boxShadow: "var(--shadow-card)",
        }}
      >
        <div style={{ marginBottom: 24 }}>
          <div
            style={{
              fontSize: 11,
              color: "var(--gold)",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              marginBottom: 10,
              fontWeight: 600,
            }}
          >
            {t.login.eyebrow}
          </div>
          <h1 className="display" style={{ margin: 0, fontSize: isMobile ? 26 : 32 }}>
            {step === "email" ? t.login.titleEmail : t.login.titleCode}
          </h1>
          <p
            style={{
              marginTop: 10,
              fontSize: 14,
              color: "var(--text-2)",
              lineHeight: 1.55,
            }}
          >
            {step === "email" ? t.login.intro : <span>{t.login.introCode(email)}</span>}
          </p>
        </div>

        {step === "email" && (
          <button
            type="button"
            onClick={() => {
              const e = email.trim();
              if (!e) {
                setError("Nejdřív vyplň e-mail.");
                return;
              }
              setError(null);
              setStep("code");
            }}
            style={{
              fontSize: 12,
              color: "var(--gold)",
              background: "transparent",
              border: 0,
              cursor: "pointer",
              padding: 0,
              marginBottom: 14,
              textAlign: "left",
            }}
          >
            {t.login.haveCode}
          </button>
        )}
        {step === "email" && (
          <form onSubmit={submitEmail}>
            <label
              htmlFor="login-email"
              style={{
                fontSize: 12,
                color: "var(--text-2)",
                marginBottom: 6,
                display: "block",
              }}
            >
              {t.login.emailLabel}
            </label>
            <input
              id="login-email"
              className="input"
              type="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t.login.emailPlaceholder}
              autoComplete="email"
              inputMode="email"
            />
            <Button
              type="submit"
              variant="primary"
              size="lg"
              disabled={submitting}
              style={{ width: "100%", marginTop: 16, justifyContent: "center" }}
            >
              {submitting ? <Ic.Loader size={16} className="animate-spin" /> : null}
              {t.login.requestCode}
              {!submitting && <Ic.ArrowRight size={16} />}
            </Button>
          </form>
        )}

        {step === "code" && (
          <form onSubmit={submitCode}>
            <input
              ref={codeInputRef}
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/[^0-9A-Za-z]/g, ""))}
              disabled={submitting}
              placeholder="12345678"
              style={{
                height: isMobile ? 52 : 60,
                width: "100%",
                background: "var(--bg-base-2)",
                border: `1px solid ${code ? "var(--gold)" : "var(--border)"}`,
                borderRadius: "var(--r-md)",
                color: "var(--text)",
                fontSize: 24,
                fontWeight: 600,
                textAlign: "center",
                letterSpacing: "0.4em",
                fontFamily: "var(--font-mono)",
                outline: "none",
                padding: "0 16px",
                boxShadow: code ? "0 0 0 4px var(--gold-12)" : "none",
                transition: "all .14s",
              }}
            />
            <Button
              type="submit"
              variant="primary"
              size="lg"
              disabled={submitting || code.trim().length < 6 || code.trim().length > 10}
              style={{ width: "100%", marginTop: 14, justifyContent: "center" }}
            >
              {submitting && <Ic.Loader size={16} className="animate-spin" />}
              Anmelden
              {!submitting && <Ic.ArrowRight size={16} />}
            </Button>
            <div
              style={{
                marginTop: 18,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                fontSize: 13,
              }}
            >
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setStep("email");
                  setCode("");
                  setError(null);
                }}
              >
                {t.login.changeEmail}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={requestResend}
                style={{ color: "var(--gold)" }}
              >
                {t.login.resendCode}
              </Button>
            </div>
          </form>
        )}

        {error && (
          <div
            role="alert"
            style={{
              marginTop: 16,
              padding: 10,
              fontSize: 12.5,
              color: "var(--red)",
              background: "var(--red-12)",
              border: "1px solid rgba(248,113,113,0.22)",
              borderRadius: "var(--r-md)",
            }}
          >
            {error}
          </div>
        )}

        <div
          style={{
            marginTop: 28,
            paddingTop: 20,
            borderTop: "1px solid var(--border)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            fontSize: 12,
            color: "var(--text-3)",
          }}
        >
          <span>{t.login.footerNote}</span>
        </div>
      </div>

      <div
        style={{
          position: "relative",
          zIndex: 1,
          marginTop: isMobile ? 28 : 48,
          fontSize: 11.5,
          color: "var(--text-3)",
          display: "flex",
          gap: 18,
          letterSpacing: "0.04em",
        }}
      >
        {t.login.footer.map((s) => (
          <span key={s}>{s}</span>
        ))}
      </div>

      <style>{`
        .animate-spin { animation: portal-spin 1s linear infinite; }
        @keyframes portal-spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
